import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X } from 'lucide-react';
import { format, parseISO } from 'date-fns';

export default function ReviewPage() {
  const { getAuthHeaders, API } = useAuth();
  const navigate = useNavigate();
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const fetchDecisions = async (query = '') => {
    try {
      setIsSearching(!!query);
      const url = query 
        ? `${API}/decisions?q=${encodeURIComponent(query)}`
        : `${API}/decisions`;
      const response = await axios.get(url, {
        headers: getAuthHeaders()
      });
      setDecisions(response.data);
    } catch (error) {
      console.error('Error fetching decisions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDecisions();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchDecisions(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const clearSearch = () => {
    setSearchQuery('');
  };

  const handleRowClick = (e, decisionId) => {
    // Don't navigate if clicking on interactive elements
    const target = e.target;
    const isInteractive = target.closest('button, a, input, [role="button"], [data-interactive]');
    if (isInteractive) return;
    
    navigate(`/decision/${decisionId}`);
  };

  const getStatus = (decision) => {
    const followUps = decision.follow_ups || [];
    if (followUps.length === 0) return 'No follow-ups';
    
    const openCount = followUps.filter(f => f.status === 'open').length;
    const doneCount = followUps.filter(f => f.status === 'done').length;
    
    if (openCount === 0) return 'Complete';
    return `${doneCount}/${followUps.length} done`;
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="review-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/" className="text-2xl font-medium tracking-tight font-serif hover:text-accent transition-colors">
            Decided
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-muted-foreground mb-8">
          A quiet look at recent decisions.
        </p>

        {/* Search */}
        <div className="relative mb-8">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground/50" />
          <Input
            type="text"
            placeholder="Search decisions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10 bg-background border-border/50 focus:border-border"
            data-testid="review-search-input"
          />
          {searchQuery && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/50 hover:text-muted-foreground transition-colors"
              data-testid="review-clear-search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-16 text-muted-foreground">
            Loading decisions...
          </div>
        ) : decisions.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            {isSearching ? 'No results found.' : 'No decisions yet.'}
          </div>
        ) : (
          <div data-testid="decisions-list">
            {/* Desktop Header - hidden on mobile */}
            <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-2 text-xs tracking-wide text-muted-foreground uppercase border-b border-border">
              <div className="md:col-span-4">Decision</div>
              <div className="md:col-span-2">Type</div>
              <div className="md:col-span-2">Status</div>
              <div className="md:col-span-2">Outcome</div>
              <div className="md:col-span-2 text-right">Date</div>
            </div>

            {/* Decision Rows */}
            {decisions.map((decision) => (
              <div 
                key={decision.id}
                onClick={(e) => handleRowClick(e, decision.id)}
                className="border-b border-border/50 hover:bg-secondary/50 transition-colors cursor-pointer"
                data-testid={`review-row-${decision.id}`}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/decision/${decision.id}`)}
              >
                {/* Mobile Layout - stacked */}
                <div className="md:hidden p-4 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-foreground font-medium flex-1">
                      {decision.title}
                    </p>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatDate(decision.date)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {decision.decision_type && (
                      <span className="capitalize">{decision.decision_type}</span>
                    )}
                    <span>{getStatus(decision)}</span>
                    {decision.outcome && (
                      <span className="capitalize">{decision.outcome}</span>
                    )}
                  </div>
                  {decision.reflection && (
                    <p className="text-xs text-muted-foreground/70 line-clamp-2">
                      "{decision.reflection}"
                    </p>
                  )}
                </div>

                {/* Desktop Layout - grid */}
                <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-4 items-start">
                  <div className="md:col-span-4 min-w-0">
                    <p className="text-foreground font-medium truncate" title={decision.title}>
                      {decision.title}
                    </p>
                    {decision.reflection && (
                      <p className="text-xs text-muted-foreground/70 mt-1 truncate" title={decision.reflection}>
                        "{decision.reflection}"
                      </p>
                    )}
                  </div>
                  <div className="md:col-span-2 text-sm text-muted-foreground capitalize">
                    {decision.decision_type || '—'}
                  </div>
                  <div className="md:col-span-2 text-sm text-muted-foreground">
                    {getStatus(decision)}
                  </div>
                  <div className="md:col-span-2 text-sm text-muted-foreground capitalize">
                    {decision.outcome || '—'}
                  </div>
                  <div className="md:col-span-2 text-sm text-muted-foreground text-right">
                    {formatDate(decision.date)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
